/**
 * Relay agent command channel shared types.
 *
 * Commands the backend publishes to a per-tenant RabbitMQ queue that the
 * on-prem Windows sync relay pulls (basic.get over amqps). The agent
 * deserializes these and dispatches them to its sync machinery.
 *
 * This Zod schema is the single source of truth for the command shape; the C#
 * agent mirrors it as a DTO (windows-agent/service/Models/RelayAgentCommand.cs).
 * Keep the two in sync.
 *
 * **The channel carries actions, not state.** Cursors, per-integration
 * `enabled`, queries, and schedules all live in the ERP, and the agent reads
 * them over the relay API every time it syncs. Pushing any of that down a queue
 * would give the same fact two owners and two clocks. So there are exactly two
 * commands, and both are things the agent could not have decided on its own:
 *
 * - `sync_now` — run this integration now, instead of at its next cron fire.
 * - `stop` — halt scheduled syncing now, instead of at the next config poll.
 *
 * Note the asymmetry: there is no `start`. Resuming is not urgent — clearing the
 * kill switch in the ERP takes effect on the agent's next poll, and `sync_now`
 * already covers "and run one immediately". Stopping is urgent, because the
 * reason you are stopping is usually that something is actively going wrong.
 */

import { z } from "zod";

export const RELAY_AGENT_COMMAND_TYPES = ["sync_now", "stop"] as const;

export const RelayAgentCommandTypeSchema = z.enum(RELAY_AGENT_COMMAND_TYPES);

export type RelayAgentCommandType = z.infer<typeof RelayAgentCommandTypeSchema>;

const CommandEnvelopeSchema = z.object({
  /** Idempotency / dedup key for this command. */
  id: z.string().uuid(),
  /** ISO-8601 timestamp of when the backend issued the command. */
  issuedAt: z.iso.datetime(),
});

/**
 * Run one integration now. Always carries an integration id: "sync everything"
 * is N of these rather than a fan-out flag, so the broker retries a failed
 * integration on its own instead of re-running the ones that already succeeded.
 */
export const RelaySyncNowCommandSchema = CommandEnvelopeSchema.extend({
  type: z.literal("sync_now"),
  /** Target relay integration id (shared by the ERP and the agent). */
  integrationId: z.string().uuid(),
});

/**
 * Halt scheduled syncing on the agent that owns this queue. Agent-wide by
 * design: pausing a single integration is a `enabled` flag in the ERP, which the
 * agent already honours on its next config read.
 */
export const RelayStopCommandSchema = CommandEnvelopeSchema.extend({
  type: z.literal("stop"),
});

/**
 * A single command message. Discriminated on `type` so `integrationId` is
 * required exactly where it means something, rather than being optional
 * everywhere and enforced by hand at both ends of the queue.
 */
export const RelayAgentCommandSchema = z.discriminatedUnion("type", [
  RelaySyncNowCommandSchema,
  RelayStopCommandSchema,
]);

export type RelayAgentCommand = z.infer<typeof RelayAgentCommandSchema>;
